import { AddIcon, CheckCircleIcon, MinusIcon } from "@chakra-ui/icons";
import {
  Box,
  Badge,
  Button,
  Code,
  HStack,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  KYDPixelDisplaySettings,
  KYDPixelSettings,
  KYDPixelSettingsUpdate,
} from "@common/interfaces/KYDEvent";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  FaGoogle,
  FaHubspot,
  FaInstagram,
  FaMailBulk,
  FaSnapchatGhost,
  FaTiktok,
  FaTrash,
  FaTwitter,
} from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";

const pixelTypes: {
  value: KYDPixelSettings["type"];
  text: string;
  icon: React.ReactNode;
}[] = [
  { value: "facebook", text: "Meta", icon: <FaInstagram size="20px" /> },
  { value: "tiktok", text: "TikTok", icon: <FaTiktok size="20px" /> },
  { value: "twitter", text: "Twitter", icon: <FaTwitter size="20px" /> },
  { value: "hubspot", text: "HubSpot", icon: <FaHubspot size="20px" /> },
  {
    value: "snapchat",
    text: "Snapchat",
    icon: <FaSnapchatGhost size="20px" />,
  },
  { value: "hive", text: "Hive", icon: <FaMailBulk size="20px" /> },
  { value: "ga", text: "Google Tag", icon: <FaGoogle size="20px" /> },
  { value: "goaudience", text: "GoAudience", icon: null },
];

type PixelFormValue = KYDPixelSettingsUpdate & {
  has_access_token: boolean;
};

const toFormValues = (
  pixels: Array<KYDPixelSettings | KYDPixelDisplaySettings>
): PixelFormValue[] =>
  pixels.map((pixel) => {
    const hasAccessToken =
      "has_access_token" in pixel
        ? pixel.has_access_token
        : Boolean(pixel.access_token);
    const { access_token: _accessToken, ...safePixel } =
      pixel as KYDPixelSettings;

    return {
      ...safePixel,
      has_access_token: hasAccessToken,
      access_token: "",
    };
  });

const MarketingPixelsEditor = ({
  pixels,
  readOnlyPixels = [],
  isLoading = false,
  readOnly = false,
  onSave,
}: {
  pixels: Array<KYDPixelSettings | KYDPixelDisplaySettings>;
  readOnlyPixels?: Array<KYDPixelSettings | KYDPixelDisplaySettings>;
  isLoading?: boolean;
  readOnly?: boolean;
  onSave: (pixels: KYDPixelSettingsUpdate[]) => Promise<void>;
}) => {
  const formValues = useMemo(() => toFormValues(pixels), [pixels]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<{ pixels: PixelFormValue[] }>({
    mode: "onChange",
    defaultValues: { pixels: formValues },
  });
  const { append, fields, remove } = useFieldArray({
    control,
    name: "pixels",
    keyName: "field_id",
  });

  useEffect(() => {
    if (!isEditing) {
      reset({ pixels: formValues });
    }
  }, [formValues, isEditing, reset]);

  const watchedPixels = watch("pixels");
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchedPixels[index],
  }));

  const submit = handleSubmit(async ({ pixels: submittedPixels }) => {
    setIsSaving(true);
    try {
      await onSave(
        submittedPixels.map(
          ({ has_access_token: _hasAccessToken, access_token, ...pixel }) => ({
            ...pixel,
            ...(access_token === null || access_token ? { access_token } : {}),
          })
        )
      );
      setIsEditing(false);
    } catch {
      // The caller owns error presentation; keep the form open for correction.
    } finally {
      setIsSaving(false);
    }
  });

  const disabled = isLoading || isSaving;

  return (
    <Box overflowX="auto">
      {!readOnly && (
        <HStack justify="flex-end">
          <IconButton
            aria-label="Edit pixels"
            icon={<FiEdit2 />}
            isDisabled={disabled || isEditing}
            onClick={() => setIsEditing(true)}
            variant="outline"
          />
          <IconButton
            aria-label="Add pixel"
            icon={<AddIcon />}
            isDisabled={disabled}
            onClick={() => {
              setIsEditing(true);
              append({
                id: "",
                type: "facebook",
                access_token: "",
                has_access_token: false,
              });
            }}
            variant="outline"
          />
        </HStack>
      )}
      <Table
        sx={{
          mt: "16px",
          opacity: disabled ? 0.5 : 1,
          borderCollapse: "collapse",
        }}
      >
        <Thead>
          <Tr>
            <Th minW="150px">Platform</Th>
            <Th minW="220px">ID</Th>
            <Th minW="280px">Conversion API access token</Th>
            {!readOnly && <Th width="40px" />}
          </Tr>
        </Thead>
        <Tbody>
          {readOnlyPixels.map((pixel) => {
            const type = pixelTypes.find((item) => item.value === pixel.type);
            const tokenConfigured =
              "has_access_token" in pixel
                ? pixel.has_access_token
                : Boolean(pixel.access_token);

            return (
              <Tr key={`read-only-${pixel.type}-${pixel.id}`}>
                <Td>
                  <HStack>
                    {type?.icon}
                    <Text fontWeight="bold">{type?.text || pixel.type}</Text>
                    <Badge>Organization</Badge>
                  </HStack>
                </Td>
                <Td>
                  <Code>{pixel.id}</Code>
                </Td>
                <Td>
                  {tokenConfigured ? (
                    <HStack>
                      <CheckCircleIcon color="green.500" />
                      <Text>Configured</Text>
                    </HStack>
                  ) : (
                    <HStack color="gray.500">
                      <MinusIcon />
                      <Text>Not configured</Text>
                    </HStack>
                  )}
                </Td>
                {!readOnly && <Td />}
              </Tr>
            );
          })}
          {controlledFields.map((pixel, index) => {
            const type = pixelTypes.find((item) => item.value === pixel.type);
            const replacementConfigured =
              typeof pixel.access_token === "string" &&
              pixel.access_token.length > 0;
            const tokenConfigured =
              pixel.access_token !== null &&
              (replacementConfigured || pixel.has_access_token);

            return (
              <Tr key={pixel.field_id}>
                <Td>
                  {isEditing ? (
                    <Select
                      {...register(`pixels.${index}.type`)}
                      isDisabled={disabled}
                    >
                      {pixelTypes.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.text}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <HStack>
                      {type?.icon}
                      <Text fontWeight="bold">{type?.text || pixel.type}</Text>
                    </HStack>
                  )}
                </Td>
                <Td>
                  {isEditing ? (
                    <Input
                      {...register(`pixels.${index}.id`, { required: true })}
                      isDisabled={disabled}
                      placeholder="Pixel ID"
                    />
                  ) : (
                    <Code>{pixel.id}</Code>
                  )}
                </Td>
                <Td>
                  {isEditing ? (
                    <HStack>
                      {/* These do not need to be password type because the tokens are hidden after they are saved, and Meta shows them in plain text in their UI. */}
                      <Input
                        {...register(`pixels.${index}.access_token`)}
                        autoComplete="new-password"
                        isDisabled={disabled}
                        placeholder={
                          pixel.has_access_token
                            ? "Enter a replacement token"
                            : "Enter an access token"
                        }
                      />
                      {tokenConfigured ? (
                        <Button
                          isDisabled={disabled}
                          onClick={() => {
                            setValue(`pixels.${index}.access_token`, null, {
                              shouldDirty: true,
                            });
                            setValue(`pixels.${index}.has_access_token`, false);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          Clear
                        </Button>
                      ) : (
                        <Text
                          color="gray.500"
                          fontSize="sm"
                          whiteSpace="nowrap"
                        >
                          Not configured
                        </Text>
                      )}
                    </HStack>
                  ) : tokenConfigured ? (
                    <HStack>
                      <CheckCircleIcon color="green.500" />
                      <Text>Configured</Text>
                    </HStack>
                  ) : (
                    <HStack color="gray.500">
                      <MinusIcon />
                      <Text>Not configured</Text>
                    </HStack>
                  )}
                </Td>
                {!readOnly && (
                  <Td>
                    {isEditing && (
                      <IconButton
                        aria-label="Remove pixel"
                        icon={<FaTrash />}
                        isDisabled={disabled}
                        onClick={() => remove(index)}
                        variant="ghost"
                      />
                    )}
                  </Td>
                )}
              </Tr>
            );
          })}
          {!readOnly &&
            readOnlyPixels.length === 0 &&
            controlledFields.length === 0 && (
              <Tr>
                <Td colSpan={4}>
                  <Text color="gray.500">No marketing pixels configured.</Text>
                </Td>
              </Tr>
            )}
        </Tbody>
      </Table>
      {!readOnly && (
        <HStack mt={4}>
          {isEditing && (
            <>
              <Button
                colorScheme="green"
                isDisabled={disabled || !isValid}
                isLoading={isSaving}
                onClick={submit}
              >
                Save
              </Button>
              <Button
                isDisabled={disabled}
                onClick={() => {
                  reset({ pixels: formValues });
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </HStack>
      )}
    </Box>
  );
};

export default MarketingPixelsEditor;
